/**
 * Per-token script detection for RTL/LTR rendering.
 *
 * Per CLAUDE.md "Hebrew↔English code-switching is a first-class concern":
 * Soniox's per-token `language` field returns the dominant language for the file,
 * not the actual script of each token. So we detect script from the characters
 * themselves, not from Soniox's metadata.
 *
 * Returns "rtl" if any Hebrew character is present in the token, otherwise "ltr".
 * Numbers and punctuation are ambiguous and bidi will handle them correctly
 * inside an isolation boundary as long as we set dir on each span.
 */
// Hebrew block (U+0590–U+05FF) + Hebrew presentation forms (U+FB1D–U+FB4F).
// NOTE: do NOT use the literal "יִ-ﭏ" as the range endpoint — יִ is two
// codepoints (YOD U+05D9 + HIRIQ U+05B4), so the range U+05B4–U+FB4F silently
// covers Arabic and other scripts. Same ranges as backend name_script.py _HEBREW.
const HEBREW_RE = /[֐-׿יִ-ﭏ]/;

// Module-level cache: same token text always maps to the same direction.
// In a typical 63-minute meeting there are only ~5 000 unique token strings,
// so this stays tiny. Bounded at 10 000 entries to prevent unbounded growth
// across long-running SPA sessions (cache clears instead of evicting).
const _tokenDirCache = new Map<string, "rtl" | "ltr">();

export function tokenDir(text: string): "rtl" | "ltr" {
  const cached = _tokenDirCache.get(text);
  if (cached !== undefined) return cached;
  const dir: "rtl" | "ltr" = HEBREW_RE.test(text) ? "rtl" : "ltr";
  if (_tokenDirCache.size >= 10_000) _tokenDirCache.clear();
  _tokenDirCache.set(text, dir);
  return dir;
}

/**
 * Paragraph-level direction for a turn.
 *
 * The per-group <span dir> wrapping inside RawTokens handles bidi
 * isolation WITHIN a line, but the parent .turn container also needs
 * dir set or the browser lays the groups out left-to-right at the
 * paragraph level. Result: a Hebrew sentence with an inline English
 * brand name like SENZEY visually scrambles -- the English fragment
 * ends up mid-Hebrew at confusing positions and a leading "2.1." gets
 * yanked to the left edge instead of trailing the Hebrew sentence.
 *
 * Rule: if Hebrew tokens outnumber Latin tokens, the paragraph is RTL.
 * If Latin tokens outnumber Hebrew, the paragraph is LTR.
 * Digit-only / punctuation-only tokens are bidi-neutral and not counted.
 *
 * Neutral-turn rule: a turn with NO strong-directional tokens (e.g. a
 * turn that is only digits or punctuation, like "121.") has no signal of
 * its own. In that case, and also for an empty token array, the function
 * returns `fallbackDir` so the caller can supply the document direction
 * and the turn inherits it instead of hard-coding LTR.
 *
 * `fallbackDir` defaults to "ltr" so all existing call sites that do not
 * pass a fallback continue to behave exactly as before.
 *
 * Token-counting (not character-counting) is intentional -- a turn
 * with one English brand inside ten Hebrew words is Hebrew-dominant
 * regardless of how many characters the English brand has.
 */
const LATIN_RE = /[A-Za-z]/;

export function turnDir(
  tokens: { text: string }[],
  fallbackDir: "rtl" | "ltr" = "ltr",
): "rtl" | "ltr" {
  let hebrew = 0;
  let latin = 0;
  for (const t of tokens) {
    if (HEBREW_RE.test(t.text)) hebrew++;
    else if (LATIN_RE.test(t.text)) latin++;
  }
  if (hebrew === 0 && latin === 0) return fallbackDir;
  return hebrew > latin ? "rtl" : "ltr";
}

/**
 * Document-level direction for an entire transcript.
 *
 * Same dominance rule as turnDir, applied across every token in
 * every turn. Used by MeetingReview to set the .transcript
 * container's direction so that the whole list of turns flows
 * consistently (speaker labels align to a single edge, English
 * side comments in a Hebrew meeting sit at the same edge as the
 * surrounding Hebrew turns, etc.).
 *
 * Per-turn turnDir still applies underneath -- an English-only
 * turn inside a Hebrew document is still rendered with internal
 * LTR token flow; documentDir governs the paragraph's *placement*,
 * not its internal text direction.
 */
export function documentDir(tokens: { text: string }[]): "rtl" | "ltr" {
  // Reuses turnDir's counting logic. Kept as a separate exported
  // function so the call sites read intentionally at each level.
  return turnDir(tokens);
}
